/**
 * C10's cache (`KLQ-20260819-004` §6).
 *
 * **Nothing polls and nothing subscribes.** A project team changes when somebody on this
 * screen changes it, so the four member mutations invalidate the member list, the two
 * invitation mutations invalidate the invitation list, and that is the entire invalidation
 * story (CLAUDE.md, data fetching).
 *
 * The one non-obvious edge: a role change **the caller makes to themselves** can change
 * their own tab row, so it invalidates the project's surfaces query too. Changing anybody
 * else's role cannot — authority is per person — and invalidating on every change would
 * refetch navigation for nothing on the common path.
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { PROJECTS_QUERY_KEY, projectSurfacesQueryOptions } from '@/features/projects';
// The firm's own people belong to B2, which owns the screen that edits them. Imported rather
// than re-declared: a second key for one resource is two answers to one question, and the
// second answer goes stale in silence (CLAUDE.md, data fetching).
import { useWorkspaceMembers as useStudioMembers } from '@/features/studio';

import type { ProjectParty, ProjectRole } from './api';
import {
  addMember,
  archiveParty,
  changeMemberRole,
  createParty,
  createPartyContact,
  deletePartyContact,
  linkParty,
  listInvitations,
  listMembers,
  listParties,
  listPartyContacts,
  listPartyLinkCandidates,
  listProjectRoles,
  listScopes,
  removeMember,
  renameParty,
  revokeInvitation,
  sendInvitation,
  setPartyRoles,
} from './api';

/** Everything C10 reads about one project hangs off the project's own prefix. */
function peopleKey(projectId: string) {
  return [...PROJECTS_QUERY_KEY, 'people', projectId] as const;
}

export function membersQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...peopleKey(projectId), 'members'] as const,
    queryFn: () => listMembers(projectId),
    staleTime: 60_000,
  });
}

export function invitationsQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...peopleKey(projectId), 'invitations'] as const,
    queryFn: () => listInvitations(projectId),
    staleTime: 60_000,
  });
}

/**
 * The caller's own company, for the add-from-company picker — see `features/studio/queries`.
 *
 * Fetched only when a drawer that needs it is open (`enabled` at the call site, and on
 * `useWorkspaceMembers` below). It is the employee directory of a whole firm and no part of
 * the screen behind the drawer shows it, so loading it with the page would be a request
 * nobody asked for on every project visit.
 */

export function scopesQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...peopleKey(projectId), 'scopes'] as const,
    queryFn: () => listScopes(projectId),
    staleTime: 60_000,
  });
}

export function useMembers(projectId: string) {
  return useQuery(membersQueryOptions(projectId));
}

export function useInvitations(projectId: string) {
  return useQuery(invitationsQueryOptions(projectId));
}

export function useScopes(projectId: string) {
  return useQuery(scopesQueryOptions(projectId));
}

/**
 * The firm's staff, for C10's add-a-colleague picker.
 *
 * Delegated to `features/studio`, which owns the resource. Since `KLQ-20260819-007` the query
 * is keyed by the company the tab is acting as, and the hook reads that itself — so a picker
 * opened on a project owned by the studio you are acting as offers that studio's people, and
 * a switch changes the answer rather than serving the previous one from cache.
 */
export function useWorkspaceMembers(enabled: boolean) {
  return useStudioMembers(enabled);
}

export function useSendInvitation(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { readonly email: string; readonly role: ProjectRole }) =>
      sendInvitation(projectId, body),
    // A `409` on a retry earns the same `409`; a `403` from the trust ladder is a decision,
    // not a transient failure. Neither is worth a second send.
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invitationsQueryOptions(projectId).queryKey });
    },
  });
}

export function useRevokeInvitation(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => revokeInvitation(projectId, invitationId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: invitationsQueryOptions(projectId).queryKey });
    },
  });
}

export function useAddMember(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { readonly user_id: string; readonly role: ProjectRole }) =>
      addMember(projectId, body),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: membersQueryOptions(projectId).queryKey });
    },
  });
}

export interface RoleChange {
  readonly membershipId: string;
  readonly role: ProjectRole;
  /** Whether the person being re-roled is the caller. See the module note. */
  readonly isSelf: boolean;
}

export function useChangeMemberRole(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, role }: RoleChange) =>
      changeMemberRole(projectId, membershipId, role),
    retry: false,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: membersQueryOptions(projectId).queryKey });
      if (variables.isSelf) {
        void queryClient.invalidateQueries({
          queryKey: projectSurfacesQueryOptions(projectId).queryKey,
        });
      }
    },
  });
}

export function useRemoveMember(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) => removeMember(projectId, membershipId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: membersQueryOptions(projectId).queryKey });
    },
  });
}

/* --- The party roster (`KLQ-20260820-007` wave 1) ------------------------------------ */

/**
 * The project's roster of firms — **reference data, fetched once and shared**.
 *
 * It is not only C10's list: `assignee_party_id` means every assignee picker in the tasks
 * surface answers *"which firms are on this project"* from this same key, and a picker is
 * drawn per row. So this is one query with a `staleTime`, exported through the feature's
 * `index.ts` for other features to compose — never a fetch per row, never a refetch on a broad
 * event, never a subscription (CLAUDE.md, data fetching). A roster changes when somebody on
 * this screen changes it, and the four mutations below invalidate exactly this key.
 *
 * `staleTime` matches the member list's minute: the two are read side by side on C10 and a
 * shorter window here would mean the roster refetching while the names beside it did not.
 */
export function partiesQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...peopleKey(projectId), 'parties'] as const,
    queryFn: () => listParties(projectId),
    staleTime: 60_000,
  });
}

/**
 * The link picker's options, and its only source (§4).
 *
 * Fetched when a picker is open and not before — like the colleague list, it exists to fill
 * one control and no part of the screen behind it shows it. `staleTime` is the same minute:
 * the candidate set moves only when the project's membership does.
 */
export function partyLinkCandidatesQueryOptions(projectId: string, enabled: boolean) {
  return queryOptions({
    queryKey: [...peopleKey(projectId), 'party-link-candidates'] as const,
    queryFn: () => listPartyLinkCandidates(projectId),
    staleTime: 60_000,
    enabled,
  });
}

export function useParties(projectId: string) {
  return useQuery(partiesQueryOptions(projectId));
}

export function usePartyLinkCandidates(projectId: string, enabled: boolean) {
  return useQuery(partyLinkCandidatesQueryOptions(projectId, enabled));
}

export function useCreateParty(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      readonly firm_id?: string;
      readonly name?: string;
      readonly note: string | null;
    }): Promise<ProjectParty> => createParty(projectId, body),
    // A `409` on the name is a decision, not a transient failure; retrying earns the same one.
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partiesQueryOptions(projectId).queryKey });
    },
  });
}

export interface PartyRename {
  readonly partyId: string;
  readonly name: string;
}

export function useRenameParty(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ partyId, name }: PartyRename) => renameParty(projectId, partyId, name),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partiesQueryOptions(projectId).queryKey });
    },
  });
}

export function useArchiveParty(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (partyId: string) => archiveParty(projectId, partyId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partiesQueryOptions(projectId).queryKey });
    },
  });
}

export interface PartyLink {
  readonly partyId: string;
  readonly workspaceId: string;
}

/**
 * Link a party to a workspace.
 *
 * Two lists change: the roster gains the reference, and the candidate list loses the firm that
 * now holds it (a workspace may back only one party on a project). Both are invalidated; the
 * member list is not, because linking admits a firm to work it already owed and adds nobody to
 * the project's team.
 */
export function useLinkParty(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ partyId, workspaceId }: PartyLink) => linkParty(projectId, partyId, workspaceId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partiesQueryOptions(projectId).queryKey });
      void queryClient.invalidateQueries({
        queryKey: partyLinkCandidatesQueryOptions(projectId, true).queryKey,
      });
    },
  });
}

/* --- Contacts and roles (`KLQ-20260820-007` wave 2) ---------------------------------- */

/**
 * Every firm's contacts, **in one query for the whole section**.
 *
 * Not a query per party: the section draws each firm's people under its row, and a hook keyed
 * by `partyId` would fire one request per row on every visit (CLAUDE.md, data fetching). The
 * list is tens of rows and unpaginated; `PartySection` groups it once.
 */
export function partyContactsQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...peopleKey(projectId), 'party-contacts'] as const,
    queryFn: () => listPartyContacts(projectId),
    staleTime: 60_000,
  });
}

/**
 * The role vocabulary — **reference data, fetched once per project session** (`MCK-20260812-006`
 * §G). It changes when somebody edits the vocabulary, not when somebody edits a party, so no
 * mutation on this screen invalidates it. `staleTime` is five minutes rather than the roster's
 * one for that reason.
 */
export function projectRolesQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: [...peopleKey(projectId), 'roles'] as const,
    queryFn: () => listProjectRoles(projectId),
    staleTime: 300_000,
  });
}

export function usePartyContacts(projectId: string) {
  return useQuery(partyContactsQueryOptions(projectId));
}

export function useProjectRoles(projectId: string) {
  return useQuery(projectRolesQueryOptions(projectId));
}

export interface NewPartyContact {
  readonly partyId: string;
  readonly name: string;
  readonly phone: string | null;
  readonly email: string | null;
}

export function useCreatePartyContact(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ partyId, name, phone, email }: NewPartyContact) =>
      createPartyContact(projectId, { project_party_id: partyId, name, phone, email }),
    // A `422` on the address is the value in the box, not a transient failure.
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: partyContactsQueryOptions(projectId).queryKey,
      });
    },
  });
}

export function useDeletePartyContact(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) => deletePartyContact(projectId, contactId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: partyContactsQueryOptions(projectId).queryKey,
      });
    },
  });
}

export interface PartyRoles {
  readonly partyId: string;
  readonly roleIds: readonly string[];
}

/**
 * State a firm's whole role set.
 *
 * Invalidates the roster, which carries `role_ids`, and not the vocabulary: the vocabulary's
 * `locked` flag can flip when the first party takes a role, but nothing on this screen reads
 * `locked`, and refetching reference data on every chip would be the broad refetch the module
 * note rules out.
 */
export function useSetPartyRoles(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ partyId, roleIds }: PartyRoles) => setPartyRoles(projectId, partyId, roleIds),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partiesQueryOptions(projectId).queryKey });
    },
  });
}
